import { Injectable } from '@angular/core';
import { PlanningDiagnosticsService } from './planning-diagnostics.service';
import { captureScreen } from './screen-capture';
import { MAX_PICTURE_BYTES } from './picture-stroke';

export interface PlanningCanvasScreenshot {
  blob: Blob;
  width: number;
  height: number;
  capturedAt: string;
}

const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;
const MAX_BYTES = 4 * 1024 * 1024;

export type CanvasEncodeResult =
  | { ok: true; screenshot: PlanningCanvasScreenshot }
  | { ok: false; reason: string; extra?: Record<string, unknown> };

/** Copies an extracted stage into a bounded PNG. Drawing happens before the promise settles. */
export function encodePlanningCanvas(
  canvas: HTMLCanvasElement,
  capturedAt = new Date().toISOString(),
): Promise<CanvasEncodeResult> {
  const scale = Math.min(1, MAX_WIDTH / canvas.width, MAX_HEIGHT / canvas.height);
  const width = Math.max(1, Math.round(canvas.width * scale));
  const height = Math.max(1, Math.round(canvas.height * scale));
  const snapshot = document.createElement('canvas');
  snapshot.width = width;
  snapshot.height = height;
  const context = snapshot.getContext('2d', { alpha: false });
  if (!context) {
    return Promise.resolve({ ok: false, reason: '2D screenshot context unavailable' });
  }
  context.drawImage(canvas, 0, 0, width, height);
  return new Promise((resolve) => {
    try {
      snapshot.toBlob((blob) => {
        if (!blob) {
          resolve({ ok: false, reason: 'canvas encoding returned no image' });
          return;
        }
        if (blob.type !== 'image/png' || blob.size > MAX_BYTES) {
          resolve({
            ok: false,
            reason: 'captured image exceeded client bounds',
            extra: { contentType: blob.type, bytes: blob.size, width, height },
          });
          return;
        }
        resolve({ ok: true, screenshot: { blob, width, height, capturedAt } });
      }, 'image/png');
    } catch (error) {
      resolve({
        ok: false,
        reason: `canvas encoding failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
    }
  });
}

@Injectable({ providedIn: 'root' })
export class PlanningCanvasScreenshotService {
  constructor(private diagnostics: PlanningDiagnosticsService) {}

  /**
   * Tab share of the current page. The granted frame includes the composited
   * planning canvas; reading the on-screen WebGL buffer does not.
   * A dismissed share prompt returns null so the report can still be sent.
   * Call this synchronously from the click that opens the share prompt.
   */
  captureTabBytes(root: HTMLElement = document.body): Promise<Uint8Array | null> {
    return captureScreen(root).catch((error: unknown) => {
      this.captureFailed(error instanceof Error ? error.message : String(error));
      return null;
    });
  }

  /**
   * Shrinks a capture that is over the editor limit so marking still works.
   * Captures within the limit are returned unchanged.
   */
  async pngForEditor(bytes: Uint8Array, capturedAt: string): Promise<Uint8Array | null> {
    if (bytes.byteLength <= MAX_PICTURE_BYTES) {
      return bytes;
    }
    const bounded = await this.screenshotFromCapture(bytes, capturedAt);
    return bounded ? new Uint8Array(await bounded.blob.arrayBuffer()) : null;
  }

  /** The PNG handed to the report after the user confirms the editor. */
  confirmPicture(
    bytes: Uint8Array,
    capturedAt: string,
  ): Promise<PlanningCanvasScreenshot | null> {
    return this.screenshotFromCapture(bytes, capturedAt).then((screenshot) => {
      if (!screenshot) {
        return null;
      }
      this.recordCapture({
        phase: 'success',
        source: 'display-media',
        bytes: screenshot.blob.size,
        width: screenshot.width,
        height: screenshot.height,
        capturedAt,
      });
      return screenshot;
    });
  }

  capture(
    host: HTMLElement | null | undefined,
    source?: HTMLCanvasElement | null,
  ): Promise<PlanningCanvasScreenshot | null> {
    const capturedAt = new Date().toISOString();
    try {
      const extracted = source && source.width > 0 && source.height > 0 ? source : null;
      const canvas = extracted ?? this.findCanvas(host);
      if (!canvas) {
        throw new Error('planning canvas not found');
      }
      if (!extracted) {
        const gl =
          canvas.getContext('webgl2') ??
          canvas.getContext('webgl') ??
          canvas.getContext('experimental-webgl');
        if (gl && (gl as WebGLRenderingContext).isContextLost()) {
          throw new Error('planning WebGL context is lost');
        }
      }

      // Copy now, before the report dialog covers the planning view.
      return encodePlanningCanvas(canvas, capturedAt).then((encoded) => {
        if (!encoded.ok) {
          this.captureFailed(encoded.reason, encoded.extra);
          return null;
        }
        const screenshot = encoded.screenshot;
        this.recordCapture({
          phase: 'success',
          bytes: screenshot.blob.size,
          width: screenshot.width,
          height: screenshot.height,
          capturedAt,
        });
        return screenshot;
      });
    } catch (error) {
      this.captureFailed(error instanceof Error ? error.message : String(error));
      return Promise.resolve(null);
    }
  }

  private async screenshotFromCapture(
    bytes: Uint8Array,
    capturedAt: string,
  ): Promise<PlanningCanvasScreenshot | null> {
    const source = new Blob([bytes.slice()], { type: 'image/png' });
    const image = await loadImage(source);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context || !canvas.width || !canvas.height) {
      this.captureFailed('tab capture produced no image');
      return null;
    }
    context.drawImage(image, 0, 0);
    const encoded = await encodePlanningCanvas(canvas, capturedAt);
    if (!encoded.ok) {
      this.captureFailed(encoded.reason, encoded.extra);
      return null;
    }
    return encoded.screenshot;
  }

  private findCanvas(host: HTMLElement | null | undefined): HTMLCanvasElement | null {
    if (!host) {
      return null;
    }
    return (
      [...host.querySelectorAll('canvas')]
        .filter((canvas) => canvas.width > 0 && canvas.height > 0)
        .sort(
          (left, right) => right.width * right.height - left.width * left.height,
        )[0] ?? null
    );
  }

  private captureFailed(reason: string, extra: Record<string, unknown> = {}): void {
    this.recordCapture({
      phase: 'failure',
      reason: reason.slice(0, 300),
      ...extra,
    });
  }

  private recordCapture(data: Record<string, unknown>): void {
    try {
      this.diagnostics.record('screenshot.capture', data);
    } catch {
      // Screenshot diagnostics are optional and must never block the report dialog.
    }
  }
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    const timeout = window.setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error('The tab capture produced no image.'));
    }, 3000);
    image.onload = () => {
      window.clearTimeout(timeout);
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      window.clearTimeout(timeout);
      URL.revokeObjectURL(url);
      reject(new Error('The tab capture produced no image.'));
    };
    image.src = url;
  });
}
